import { FC, useMemo, useState } from 'react';
import { Table, PrintHeader, ImageModal } from '../../../components';
import { Button, Box, Divider, Typography } from '@mui/material';
import { formatMoney } from '../../../helpers';
import { ICompiledSpecOption } from '../../../models';
import { Print } from '@mui/icons-material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';

interface ISelectedOptionsTable {
  selectedOptions: ICompiledSpecOption[];
  title?: string;
  showTitle?: boolean;
  showPriceColumn?: boolean;
  subHeaderContent?: JSX.Element;
}

export const SelectedOptionsTable: FC<ISelectedOptionsTable> = ({
  selectedOptions,
  title,
  showTitle = true,
  showPriceColumn = true,
  subHeaderContent
}) => {
  const classes = useStyles();
  /**
   * The imageURL of the build option the user clicked to view a larger image of.
   */

  const [modalImageSrc, setModalImageSrc] = useState<string | null>(null);
  const columns = useMemo(() => {
    return [
      {
        Header: 'Category',
        accessor: 'category',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: ICompiledSpecOption } };
        }) => {
          const isContractOptionDefined = typeof original?.isContractOption !== 'undefined';

          return (
            <>
              {isContractOptionDefined ? (
                original.isContractOption ? (
                  <span>{original.category} - Contract</span>
                ) : (
                  <span>{original.category} - Ten Day</span>
                )
              ) : (
                <span>{original.category}</span>
              )}
            </>
          );
        }
      },
      {
        Header: 'Sub Category',
        accessor: 'subCategory',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: ICompiledSpecOption } };
        }) => {
          return (
            <>
              {!original?.subCategoryNote && <>{original?.subCategory}</>}
              {original?.subCategoryNote && (
                <>
                  <strong>{original?.subCategory}</strong>
                  <br />
                  <span>{original?.subCategoryNote}</span>
                </>
              )}
            </>
          );
        }
      },
      {
        Header: 'Name',
        accessor: 'name',
        sort: false
      },
      {
        Header: 'Qty',
        accessor: 'quantity',
        sort: false,
        overrideWidth: 75
      },
      showPriceColumn && {
        Header: 'Price',
        accessor: 'price',
        sort: false,
        isRate: true,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: ICompiledSpecOption } };
        }) => {
          return <>{original?.price ? formatMoney(original.price) : '$0.00'}</>;
        }
      },
      {
        Header: 'Photo(s)',
        accessor: 'photo',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: ICompiledSpecOption } };
        }) => {
          return (
            <Box display="flex" flexWrap="wrap">
              {original?.images?.length > 0 &&
                original?.images.map((image, index) => {
                  return (
                    <Box m={0.5} key={`${index}`}>
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        {image.description}
                      </Typography>
                      <picture
                        key={`${index}`}
                        className={
                          original.category === 'Exterior Color Scheme'
                            ? classes.smallPic
                            : classes.picture
                        }
                        onClick={() => setModalImageSrc(image?.imageUrl)}
                      >
                        <img
                          src={image?.imageUrl}
                          alt={`${image.text}`}
                          className={
                            original.category === 'Exterior Color Scheme'
                              ? classes.smOptionImage
                              : classes.optionImage
                          }
                        />
                      </picture>
                    </Box>
                  );
                })}
            </Box>
          );
        }
      }
    ].filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPriceColumn]);
  const sortByCategory = (a, b) => {
    let fa = a.category.toLowerCase(),
      fb = b.category.toLowerCase();

    if (fa < fb) {
      return -1;
    }
    if (fa > fb) {
      return 1;
    }
    return 0;
  };
  const filteredOptions = useMemo(() => {
    const colorSchemeOptions = selectedOptions.filter(o => o.category === 'Color Scheme Options');
    const extColorSchemeOptions = selectedOptions.filter(
      o => o.category === 'Exterior Color Scheme'
    );
    const restOfOptions = selectedOptions
      .filter(o => o.category !== 'Color Scheme Options' && o.category !== 'Exterior Color Scheme')
      .sort(sortByCategory);
    return [...restOfOptions, ...extColorSchemeOptions, ...colorSchemeOptions].filter(
      o => !o.name.includes('No Upgrade Wanted')
    );
  }, [selectedOptions]);
  return (
    <div className={classes.print}>
      <PrintHeader />
      <Box
        margin={subHeaderContent ? 0 : '1rem 0'}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <div>{title && showTitle && <Typography variant="h5">{title}</Typography>}</div>
        <Button
          startIcon={<Print />}
          variant="contained"
          onClick={() => {
            window.print();
          }}
        >
          Print
        </Button>
      </Box>
      {subHeaderContent}
      <Divider />
      <Table
        columns={columns}
        data={filteredOptions}
        isLoading={false}
        hidePagination
        containerClasses={classes.alignTop}
      />
      {!!modalImageSrc && (
        <ImageModal
          isOpen={!!modalImageSrc}
          onClose={() => setModalImageSrc(null)}
          source={modalImageSrc}
        />
      )}
    </div>
  );
};

const useStyles = makeStyles((theme: Theme) => {
  return {
    print: {
      '@media print': {
        height: '100%',
        width: '100%',
        maxWidth: 'none',
        maxHeight: 'none',
        margin: 0,
        background: '#fff',
        position: 'absolute',
        left: 0,
        top: 0,
        right: 0,
        bottom: 0,
        zIndex: 1300 // same as modal
      }
    },
    title: {
      marginTop: '1rem'
    },
    smallPic: {
      userSelect: 'none',
      cursor: 'pointer',
      position: 'relative',
      '&:hover::before': {
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        color: theme.palette.common.white,
        content: '"Larger"',
        fontSize: '0.7rem',
        position: 'absolute',
        textAlign: 'center',
        height: 30,
        width: 30,
        padding: 4,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }
    },
    picture: {
      userSelect: 'none',
      cursor: 'pointer',
      position: 'relative',
      '&:hover::before': {
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        color: theme.palette.common.white,
        content: '"See Larger"',
        fontSize: '0.85rem',
        position: 'absolute',
        textAlign: 'center',
        height: 125,
        width: 125,
        padding: 4,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }
    },
    optionImage: { cursor: 'pointer', height: 125, width: 125 },
    smOptionImage: { cursor: 'pointer', height: 30, width: 30 },
    alignTop: {
      '&& tbody td': {
        verticalAlign: 'top'
      }
    }
  };
});
